import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding Recap 48: JavaScript async/await and Morty's Mindblowers",
  "description": "",
  "published": "2017-10-02T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-recap-48-javascript-asyncawait-mortys-mindblowers/swizec/7829"],
  "categories": "Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should follow `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "a4g81widnGM",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`This Sunday, I created an excuse to learn JavaScript async/await: `}<a parentName="p" {...{
        "href": "http://mortysmindblowers.fun"
      }}>{`Morty's Mindblowers`}</a>{`.`}</p>
    <p>{`Async/await is meant to be the bestest way to deal with asynchronous code in JavaScript. Even better than promises. And since the web is littered with async/await tutorials, it must be really hard, right?`}</p>
    <p>{`¯`}{`\\`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p>{`No… `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/javascript-promises-monads/swizec/7814"
      }}>{`just like Promises`}</a>{`, it's nothing but a monad. A nice syntax sugarified monad, but still a monad. You can learn how to use async/await in 10 seconds.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`I'm kind of disappointed in how simple async/await is.`}<br parentName="p"></br><br parentName="p"></br>{`It's just a monad with some syntax sugar :D`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/914613801785319425"
      }}>{`October 1, 2017`}</a></blockquote>
    <p><a parentName="p" {...{
        "href": "https://youtu.be/a4g81widnGM?t=2070"
      }}>{`Watch`}</a>{`. Read 👇`}</p>
    <p>{`If you know Promises, you know async/await. Congratz! 👏`}</p>
    <p>{`Let's say you want to fetch a list of images from Imgur and narrow it down to videos. With Promises, it looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Imgur {
  static URL = "https://api.imgur.com/3/";
  static CLIENT_ID = "c848e36012571f2";

  static gifs(page) {
    return fetch(\`\${Imgur.URL}gallery/hot/rising/\${page}\`, {
      headers: { Authorization: \`Client-ID \${Imgur.CLIENT_ID}\` },
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          return json.data.filter(({ type }) => type === "video/mp4");
        } else {
          throw new Error(json.data.error);
        }
      });
  }
}
`}</code></pre>
    <p>{`We have a static `}<inlineCode parentName="p">{`gifs()`}</inlineCode>{` method that uses `}<inlineCode parentName="p">{`fetch`}</inlineCode>{` to talk to Imgur and return a Promise. The promise resolves with either a list of Imgur videos, or it rejects with an API error.`}</p>
    <p>{`Using `}<inlineCode parentName="p">{`static`}</inlineCode>{` lets us call this API without instantiating an object. `}<inlineCode parentName="p">{`Imgur.gifs()`}</inlineCode>{` for instance. Great for when you're using classes to group stuff and don't need objects.`}</p>
    <p>{`Ok, so this code is pretty readable, right? Fetch data, then parse it, then do things.`}</p>
    <p>{`With async/await, that same code looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Imgur {
    static URL = 'https://api.imgur.com/3/';
    static CLIENT_ID = '<your_id>';

    static async gifs(page) {
        const res = await fetch(\`\${Imgur.URL}gallery/hot/rising/\${page}\`,
                                {headers: { Authorization: \`Client-ID \${Imgur.CLIENT_ID}\`}}),
              json = await res.json();

        if (json.success) {
            return json.data.filter(({ type }) => type === 'video/mp4');
        }else{
            throw new Error(json.data.error);
        }
    }
}
</your_id>
`}</code></pre>
    <p>{`I don't know if that's more readable, but I can see the appeal. Your code looks just like any other code. No need to think about async stuff at all.`}</p>
    <p>{`You have to put `}<inlineCode parentName="p">{`async`}</inlineCode>{` in front of your function name. That wraps it in a Promise. Anything you return is in a Promise monad. Time bubble to use the metaphor from my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/javascript-promises-monads/swizec/7814"
      }}>{`JavaScript Promises in 2 minutes`}</a>{` article.`}</p>
    <p>{`You peek into that monad using the `}<inlineCode parentName="p">{`await`}</inlineCode>{` keyword. It's like wrapping your code in a `}<inlineCode parentName="p">{`.then`}</inlineCode>{` callback.`}</p>
    <p>{`That's really all there is to it. Once you say `}<inlineCode parentName="p">{`json = await res.json()`}</inlineCode>{`, anything that uses that `}<inlineCode parentName="p">{`json`}</inlineCode>{` variable gets wrapped in a `}<inlineCode parentName="p">{`.then()`}</inlineCode>{` call.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/10/This-subreddits-reaction-when-asked-how-we-make-our-gifs-look-so-damn-good-Imgur.gif",
        "alt": null
      }}></img></p>
    <p>{`Any time you use the `}<inlineCode parentName="p">{`Imgur.gifs()`}</inlineCode>{` method, you have to say `}<inlineCode parentName="p">{`await`}</inlineCode>{` and your function must be wrapped in `}<inlineCode parentName="p">{`async`}</inlineCode>{`.`}</p>
    <p>{`Oh, and you can wrap built-in React lifecycle hooks in `}<inlineCode parentName="p">{`async`}</inlineCode>{` and it works as expected. That part is neat. However, you can't have async getters. That would be cool.`}</p>
    <p>{`So yeah, we got the JavaScript async/await stuff working in a couple minutes, then we spent some half an hour figuring out how Imgur's API works, and an hour or two trying to use flexbox to make the `}<inlineCode parentName="p">{`<video>`}</inlineCode>{` tag shrink and grow.`}</p>
    <p>{`And in the end, we got `}<a parentName="p" {...{
        "href": "http://mortysmindblowers.fun"
      }}>{`Morty's Mindblowers`}</a>{`. It uses cut scenes from an episode of Rick & Morty to show you random gifs from Imgur's frontpage.`}</p>
    <p>{`Enjoy 🤘`}</p>
    <p><a parentName="p" {...{
        "href": "http://mortysmindblowers.fun"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2017/10/mortystill-1024x577.png",
          "alt": null
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      